// Driving a run (docs/spec/transport.md slice 7): the pure parts, shared by the worker, the staff
// app's drive screen and the tests. Which stop is next and what the driver does there; where the
// bus is on the directions' line, the next turn, and whether the bus has left the line.
import { metres } from "./eta.ts";

export type Status = "waiting" | "on" | "off" | "absent" | "held";
export interface DriveStop { id: string; seq: number; name: string; time: string | null; site_id: string | null; lat?: number | null; lng?: number | null }
/** `not_riding`: a parent said not on the bus today (school rules): nothing to do for them on this run. */
export interface DriveRider { child_id: string; stop_id: string; status: Status; meet_required: boolean; not_riding?: boolean }
export interface DriveRoute { kind: "am" | "pm" | "trip"; stops: DriveStop[] }

/** What the driver does for this child at this stop now, if anything. The Bus tab's buttons follow it. */
export type Action = "board" | "alight-school" | "alight" | "handover";
export function actionAt(route: DriveRoute, stop: DriveStop, x: DriveRider): Action | null {
  if (x.not_riding && x.status === "waiting") return null;
  const atSchool = !!stop.site_id;
  const boardHere = route.kind === "trip" || (route.kind === "am" ? !atSchool : atSchool);
  if (x.status === "waiting") return boardHere && (atSchool || x.stop_id === stop.id) ? "board" : null;
  if (x.status !== "on" && x.status !== "held") return null;
  // Arriving in the morning, back from a trip, or a child kept on the bus in the afternoon: off to staff.
  if (atSchool) return route.kind === "pm" && x.status === "on" ? null : "alight-school";
  // A home stop: nobody gets off there in the morning, and a child kept on the bus goes back to school.
  if (route.kind === "am" || x.status === "held" || x.stop_id !== stop.id) return null;
  return x.meet_required ? "handover" : "alight";
}

/** The children with something to do at a stop. */
export const dueAt = <R extends DriveRider>(route: DriveRoute, stop: DriveStop, riders: R[]) => riders.filter((x) => actionAt(route, stop, x) !== null);

/**
 * The next stop to drive to: the first, in order, with a child to pick up, drop or hand over. The
 * school counts in the morning once anyone is on board, and first in the afternoon while children
 * wait there. Null when the run's work is done.
 */
export function nextStop<R extends DriveRider>(route: DriveRoute, riders: R[]): DriveStop | null {
  const stops = [...route.stops].sort((a, b) => a.seq - b.seq);
  if (route.kind === "pm") {
    const school = stops.find((s) => s.site_id);
    // Afternoon: board at school first, then every home stop in order; a child kept on the bus
    // (nobody approved at their stop) goes back to school only after the other drops (PMU-221).
    if (school && dueAt(route, school, riders).some((x) => x.status === "waiting")) return school;
    const home = stops.find((s) => !s.site_id && dueAt(route, s, riders).length);
    if (home) return home;
    return school && dueAt(route, school, riders).length ? school : null;
  }
  for (const s of stops) {
    if (s.site_id && route.kind === "am" && riders.some((x) => x.status === "waiting" && stops.some((h) => h.id === x.stop_id && h.seq < s.seq))) continue;
    if (dueAt(route, s, riders).length) return s;
  }
  return null;
}

// ---------- the directions' line ----------

/** Valhalla's encoded shape (polyline, 6 decimal places) as [lng, lat] pairs, as the map wants them. */
export function decodeShape(s: string): [number, number][] {
  const out: [number, number][] = [];
  let i = 0, lat = 0, lng = 0;
  const next = () => { let r = 0, shift = 0, b: number; do { b = s.charCodeAt(i++) - 63; r |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20); return r & 1 ? ~(r >> 1) : r >> 1; };
  while (i < s.length) { lat += next(); lng += next(); out.push([lng / 1e6, lat / 1e6]); }
  return out;
}

export interface Maneuver { type: number; instruction: string; distance_mi: number; time_s: number; begin: number; alert: string | null; pre: string; street: string | null }
export interface Directions { stop: { id: string; name: string; lat: number; lng: number }; distance_mi: number; time_s: number; shape: [number, number][]; maneuvers: Maneuver[] }

const pt = ([lng, lat]: [number, number]) => ({ lat, lng });

/** Where a point falls on the line: the segment, how far along it (0-1), metres off the line and from its start. */
export function snap(shape: [number, number][], at: { lat: number; lng: number }) {
  let best = { seg: 0, t: 0, off: Infinity, along: 0 };
  let run = 0;
  // Local flat projection: metres per degree at this latitude, plenty accurate over a few km.
  const ky = 111_320, kx = 111_320 * Math.cos((at.lat * Math.PI) / 180);
  for (let i = 0; i < shape.length - 1; i++) {
    const a = shape[i]!, b = shape[i + 1]!;
    const ax = a[0] * kx, ay = a[1] * ky, bx = b[0] * kx, by = b[1] * ky, px = at.lng * kx, py = at.lat * ky;
    const dx = bx - ax, dy = by - ay, len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2)) : 0;
    const off = Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
    const segLen = Math.sqrt(len2);
    if (off < best.off) best = { seg: i, t, off, along: run + t * segLen };
    run += segLen;
  }
  if (shape.length === 1) best = { seg: 0, t: 0, off: metres(pt(shape[0]!), at), along: 0 };
  return best;
}

/** Metres along the line to each point (the first is 0). */
export function cumulative(shape: [number, number][]) {
  const out = [0];
  for (let i = 1; i < shape.length; i++) out.push(out[i - 1]! + metres(pt(shape[i - 1]!), pt(shape[i]!)));
  return out;
}

/** Off the line by more than this, and the phone asks for new directions. */
export const OFF_ROUTE_M = 45;
/** This close to the stop, the stop's children come up. */
export const ARRIVED_M = 45;

export interface Guidance {
  /** The turn coming up (or "arrive"), how far to it, and the rest of the way. */
  next: Maneuver | null; toNextM: number; remainingM: number;
  /** The turn after the next one and how far past it: shown as "then …" when it comes close behind. */
  after: Maneuver | null; afterM: number;
  offRoute: boolean; arrived: boolean;
  /** Where the bus is, snapped onto the line when close to it. */
  onLine: { lat: number; lng: number };
}

export function guidance(d: Directions, at: { lat: number; lng: number; accuracy?: number | null }, cum = cumulative(d.shape)): Guidance {
  const s = snap(d.shape, at);
  const total = cum[cum.length - 1] ?? 0;
  const tolerance = OFF_ROUTE_M + Math.min(50, at.accuracy ?? 0);
  const k = d.maneuvers.findIndex((m) => m.begin > s.seg);
  const next = k >= 0 ? d.maneuvers[k]! : null;
  const toNextM = next ? Math.max(0, (cum[next.begin] ?? total) - s.along) : Math.max(0, total - s.along);
  const a = d.shape[s.seg]!, b = d.shape[Math.min(s.seg + 1, d.shape.length - 1)]!;
  const after = k >= 0 ? d.maneuvers[k + 1] ?? null : null;
  const afterM = next && after ? Math.max(0, (cum[after.begin] ?? total) - (cum[next.begin] ?? total)) : Infinity;
  return {
    next, toNextM, remainingM: Math.max(0, total - s.along), after, afterM,
    offRoute: s.off > tolerance,
    arrived: metres(at, d.stop) <= ARRIVED_M + Math.min(30, at.accuracy ?? 0),
    onLine: s.off <= tolerance ? { lng: a[0] + (b[0] - a[0]) * s.t, lat: a[1] + (b[1] - a[1]) * s.t } : { lat: at.lat, lng: at.lng },
  };
}

/** The way the line goes from a point on it: towards the point `ahead` metres further along (not one tiny segment). */
export function lineBearing(shape: [number, number][], cum: number[], at: { lat: number; lng: number }, ahead = 40) {
  if (shape.length < 2) return 0;
  const s = snap(shape, at);
  const target = s.along + ahead;
  let i = cum.findIndex((d) => d >= target);
  if (i < 0) i = shape.length - 1;
  const a = shape[s.seg]!, b = shape[Math.min(s.seg + 1, shape.length - 1)]!;
  const from = { lng: a[0] + (b[0] - a[0]) * s.t, lat: a[1] + (b[1] - a[1]) * s.t };
  const to = { lng: shape[i]![0], lat: shape[i]![1] };
  return from.lng === to.lng && from.lat === to.lat ? bearing({ lng: a[0], lat: a[1] }, { lng: b[0], lat: b[1] }) : bearing(from, to);
}

/** US road distances: feet under a tenth of a mile (in 50s), then miles. */
export function distanceLabel(m: number): string {
  const mi = m / 1609.344;
  if (mi < 0.1) return `${Math.max(50, Math.round((m * 3.28084) / 50) * 50)} ft`;
  return `${mi < 10 ? mi.toFixed(1) : Math.round(mi)} mi`;
}

/** Degrees clockwise from north, from one point to the next. */
export function bearing(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const r = Math.PI / 180;
  const y = Math.sin((b.lng - a.lng) * r) * Math.cos(b.lat * r);
  const x = Math.cos(a.lat * r) * Math.sin(b.lat * r) - Math.sin(a.lat * r) * Math.cos(b.lat * r) * Math.cos((b.lng - a.lng) * r);
  return ((Math.atan2(y, x) / r) + 360) % 360;
}
